import { useEffect, useRef } from "react";
import { Box, Alert, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import DocumentRepositoryTable from "./Table/DocumentRepositoryTable";
import { ContentHeader } from "../ContentHeader";
import { useDocumentRepository } from "./hooks/useDocumentRepository";
import DocumentRepositoryFilter from "./components/DocumentRepositoryFilter";
import DocumentRepositoryPagination from "./components/DocumentRepositoryPagination";
import { DownloadButton } from "./components/DownloadButton";
import { useAppDispatch } from "@/hooks";
import { messageDocumentRepositoryAttachment } from "./actions/attachments";


const DocumentRepository = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const downloadMessageRef = useRef(null);
  const downloadMessage = useSelector(
    (state) => state.documentRepository.download_message
  );
  const isFetching = useSelector(
    (state) => state.documentRepository.isFetching
  );
  const {
    items,
    categoryTabs,
    activeCategoryCode,
    loaded,
    errors,
    initialFilter,
    initialItemsPage,
    handleItemsOffset,
    handleCategoryChange,
    applySearchFilter,
    fetchData,
    selectedAttachments,
    hasVisibleAttachments,
    hasSelectedAttachments,
    isAllVisibleSelected,
    selectedAttachmentIds,
    toggleAttachmentSelection,
    toggleAllVisibleSelection,
  } = useDocumentRepository()
  const showBackgroundUpdating = isFetching && items.length > 0;

  useEffect(() => {
    if (!downloadMessage || !downloadMessageRef.current) {
      return;
    }

    const prefersReducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;

    requestAnimationFrame(() => {
      downloadMessageRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
      downloadMessageRef.current?.focus?.({ preventScroll: true });
    });
  }, [downloadMessage]);

  return (
    <>
      <ContentHeader
        title={t('common:text.document_repository_view_title')}
        addButton={false}
        refreshButton={true}
        refreshDisabled={isFetching}
        refreshClickMethod={fetchData}
      />

      {!errors ? 
        <div>
          {downloadMessage && (
            <Alert
              ref={downloadMessageRef}
              sx={{ mb: 2 }}
              severity="warning"
              tabIndex={-1}
              onClose={() => dispatch(messageDocumentRepositoryAttachment(null))}
            >
              {downloadMessage}
            </Alert>
          )}

          {categoryTabs.length > 1 && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={activeCategoryCode}
                onChange={handleCategoryChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                {categoryTabs.map((category) => (
                  <Tab
                    key={category.code}
                    label={category.name}
                    value={category.code}
                  />
                ))}
              </Tabs>
            </Box>
          )}

          <DocumentRepositoryFilter 
            initialFilters={initialFilter} 
            initialItemsPage={initialItemsPage}
            applySearchFilter={applySearchFilter}
          />

          {showBackgroundUpdating && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
              }}
              aria-live="polite"
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {t('common:text.document_repository_updating_list')}
              </Typography>
            </Box>
          )}

          {loaded && items.length === 0 ? (
            <Alert sx={{mb: 2}} severity="info">
              {t('common:text.document_repository_view_empty_list')}
            </Alert>
          ) : (
            <>
              <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
                <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', mt: 1 }}>
                  <DocumentRepositoryPagination onItemsOffsetChange={handleItemsOffset}/>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedAttachmentIds.length > 0
                      ? t('common:text.document_repository_selected_count', { count: selectedAttachmentIds.length })
                      : ''}
                  </Typography>
                  <DownloadButton
                    attachments={selectedAttachments}
                    disabled={!hasSelectedAttachments}
                    buttonText={t('common:text.document_repository_download_selected')}
                    title={t('common:text.document_repository_download_selected')}
                  />
                </Box>
              </Box>
              <DocumentRepositoryTable
                items={items}
                selectedAttachmentIds={selectedAttachmentIds}
                hasVisibleAttachments={hasVisibleAttachments}
                isAllVisibleSelected={isAllVisibleSelected}
                toggleAttachmentSelection={toggleAttachmentSelection}
                toggleAllVisibleSelection={toggleAllVisibleSelection}
              />
              <Box sx={{ position: 'relative', width: '100%' }}>
                <Box sx={{ mt: 2 }}>
                  <DocumentRepositoryPagination onItemsOffsetChange={handleItemsOffset}/>
                </Box>
              </Box>
            </>
          )}
        </div>
      :
        <Alert severity="warning">{t("common:text.document_repository_error_fetching")}</Alert>
      }
        
    </>
  );
}

export default DocumentRepository;
